import { Injectable } from "@angular/core";
import { MessageTypes } from "../core/services/constants";
import { NgxMasonryComponent } from "ngx-masonry";
import Swal from "sweetalert2";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  masonryComponent: NgxMasonryComponent;
  constructor() {}
  isFileSizeValid(type: string, size: number): boolean {
    if (
      ((type === MessageTypes.VIDEO || type === MessageTypes.DOCUMENT) &&
        size >= 52428800) ||
      (type !== MessageTypes.VIDEO &&
        type !== MessageTypes.DOCUMENT &&
        size >= 20971520)
    ) {
      return false;
    }
    return true;
  }

  resizeMedia(width: number, height: number) {
    const min = 100;
    const max = 280;

    const aspect = width / height;
    if (height < width) {
      if (height < min) {
        height = min;
        width = height * aspect;
      }
    } else if (width < min) {
      width = min;
      height = width / aspect;
    }

    if (height > width) {
      if (height > max) {
        height = max;
        width = height * aspect;
      }
    } else if (width > max) {
      width = max;
      height = width / aspect;
    }

    return [width, height];
  }

  dateFormat(date: string) {
    const now = new Date(date);
    // Get individual components
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(now.getDate()).padStart(2, "0");

    // Format as YYYY-MM-DD
    const formattedDate = `${year}-${month}-${day}`;

    return formattedDate;
  }

  isMimeTypeValid(msgType: string, mimeType: string): boolean {
    if (!mimeType) {
      return false;
    } else if (msgType !== MessageTypes.DOCUMENT) {
      return (
        [
          "image/bmp",
          "image/jpeg",
          "image/png",
          "image/gif",
          "audio/mid",
          "audio/midi",
          "audio/mp4",
          "audio/mpeg",
          "audio/ogg",
          "audio/basic",
          "audio/mp3",
          "video/mp4",
          "video/mpeg",
          "video/ogg",
        ].indexOf(mimeType) !== -1
      );
    } else if (msgType === MessageTypes.DOCUMENT) {
      return (
        [
          "text/plain",
          "application/rtf",
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/zip",
          "application/x-tar",
          "application/x-7z-compressed",
          "application/x-zip-compressed",
          "application/x-rar-compressed",
          "application/vnd.android.package-archive",
        ].indexOf(mimeType) !== -1
      );
    }
  }

  fileChecker(file, type) {
    if (!file.name) {
      Swal.fire("Upload failed", "File name is missing.", "error");
      return false;
    } else if (file.size <= 0) {
      Swal.fire("Upload failed", "File is empty.", "error");
      return false;
    } else if (!this.isMimeTypeValid(type, file.type)) {
      Swal.fire("Upload failed", "Unsupported file type.", "error");
      return false;
    } else if (!this.isFileSizeValid(type, file.size)) {
      if (type === MessageTypes.VIDEO) {
        Swal.fire("Upload failed", "File size exceeds 50MB.", "error");
      } else {
        Swal.fire("Upload failed", "File size exceeds 20MB.", "error");
      }
      return false;
    } else {
      return true;
    }
  }

  updateTimeFromString(timeString: string): Date | null {
    if (!timeString) {
      return null; // Return null if the input is invalid
    }

    const currentDate = new Date(); // Get the current date

    // Check if the time string is in 12-hour format with AM/PM
    const timeRegex12Hour = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;
    const timeRegex24Hour = /^(\d{1,2}):(\d{2})$/;

    let hours: number;
    let minutes: number;

    if (timeRegex12Hour.test(timeString)) {
      // Parse 12-hour format (e.g., "03:00 PM")
      const match = timeString.match(timeRegex12Hour);
      if (match) {
        hours = parseInt(match[1], 10);
        minutes = parseInt(match[2], 10);
        const isPM = match[3].toUpperCase() === "PM";

        // Convert to 24-hour format
        if (isPM && hours < 12) {
          hours += 12;
        } else if (!isPM && hours === 12) {
          hours = 0; // Handle 12 AM case
        }
      }
    } else if (timeRegex24Hour.test(timeString)) {
      // Parse 24-hour format (e.g., "15:25")
      const match = timeString.match(timeRegex24Hour);
      if (match) {
        hours = parseInt(match[1], 10);
        minutes = parseInt(match[2], 10);
      }
    } else {
      console.error("Invalid time format:", timeString);
      return null; // Return null if the format is invalid
    }

    // Update the time
    currentDate.setHours(hours);
    currentDate.setMinutes(minutes);
    currentDate.setSeconds(0); // Optional: Reset seconds to 0
    currentDate.setMilliseconds(0); // Optional: Reset milliseconds to 0

    return currentDate;
  }

  parseAndFormatDate(
    dateString: string,
    inputFormat: string,
    outputFormat: string = "yyyy-MM-dd"
  ): Date | null {
    if (!dateString || !inputFormat) {
      return null; // Return null if input is invalid
    }

    // Split the input format and date string into parts
    const formatParts = inputFormat.split(/[-/]/);
    const dateParts = dateString.split(/[-/]/);

    if (formatParts.length !== dateParts.length) {
      return null; // Return null if the format doesn't match the date string
    }

    // Map the date parts to day, month, and year
    const dateMap: { [key: string]: string } = {};
    formatParts.forEach((part, index) => {
      dateMap[part] = dateParts[index];
    });

    // Extract day, month, and year
    const day = dateMap["dd"] || dateMap["d"];
    const month = dateMap["MM"] || dateMap["M"];
    const year = dateMap["yyyy"] || dateMap["yy"];

    if (!day || !month || !year) {
      return null; // Return null if any part is missing
    }

    // Convert to a valid date object
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
    if (isNaN(parsedDate.getTime())) {
      return null; // Return null if the date is invalid
    }

    // Format the date to the desired output format
    return parsedDate;
  }
}
